import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { APIClient } from '../../services/api-client';
import { Play, Square, RotateCcw, Settings } from 'lucide-react';

interface Service {
  id: string;
  name: string;
  status: 'running' | 'stopped' | 'error';
  type: string;
  port: number;
  health: string;
}

interface APIResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

interface ServiceControlPanelProps {
  className?: string;
}

export const ServiceControlPanel: React.FC<ServiceControlPanelProps> = ({ className }) => {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [selectedService, setSelectedService] = useState<string | null>(null);

  const apiClient = new APIClient();

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get<APIResponse<{services: Service[]}>>('/api/v1/services/');
      if (response.success && response.data) {
        setServices(response.data.services || []);
      } else {
        setError('Failed to load services');
      }
    } catch (err) {
      setError('Error loading services');
    } finally {
      setLoading(false);
    }
  };

  const handleServiceAction = async (serviceId: string, action: 'start' | 'stop' | 'restart') => {
    try {
      setActionLoading(serviceId);
      const response = await apiClient.post<APIResponse<any>>(`/api/v1/services/${serviceId}/${action}`);
      
      if (response.success) {
        // Reload services to get updated status
        await loadServices();
      } else {
        setError(`Failed to ${action} service`);
      }
    } catch (err) {
      setError(`Error ${action}ing service`);
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running':
        return 'bg-green-500';
      case 'stopped':
        return 'bg-red-500';
      case 'error':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'running':
        return 'Running';
      case 'stopped':
        return 'Stopped';
      case 'error':
        return 'Error';
      default:
        return 'Unknown';
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent, serviceId: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setSelectedService(serviceId);
    }
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>Service Control Panel</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32" role="status" aria-live="polite">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" aria-label="Loading services"></div>
            <span className="sr-only">Loading services...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>Service Control Panel</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-red-500 text-center" role="alert" aria-live="assertive">{error}</div>
          <Button onClick={loadServices} className="mt-2">Retry</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" aria-hidden="true" />
          Service Control Panel
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4" role="list" aria-label="Service list">
          {services.length === 0 ? (
            <div className="text-center text-gray-500 py-8" role="status">
              No services found
            </div>
          ) : (
            services.map((service) => (
              <div 
                key={service.id} 
                className={`border rounded-lg p-4 transition-colors ${
                  selectedService === service.id ? 'ring-2 ring-blue-500 bg-blue-50' : ''
                }`}
                role="listitem"
                tabIndex={0}
                onKeyDown={(e) => handleKeyDown(e, service.id)}
                onClick={() => setSelectedService(service.id)}
                aria-label={`Service: ${service.name}, Status: ${getStatusText(service.status)}, Type: ${service.type}, Port: ${service.port}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-lg">{service.name}</h3>
                    <p className="text-sm text-gray-600">
                      {service.type} • Port {service.port}
                    </p>
                  </div>
                  <div role="status" aria-live="polite">
                    <Badge className={`${getStatusColor(service.status)} text-white`}>
                      {getStatusText(service.status)}
                    </Badge>
                  </div>
                </div>
                
                <div className="flex gap-2" role="group" aria-label={`Actions for ${service.name}`}>
                  <Button
                    size="sm"
                    onClick={() => handleServiceAction(service.id, 'start')}
                    disabled={service.status === 'running' || actionLoading === service.id}
                    className="flex items-center gap-1"
                    aria-label={`Start ${service.name}`}
                  >
                    <Play className="h-4 w-4" aria-hidden="true" />
                    Start
                  </Button>
                  
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleServiceAction(service.id, 'stop')}
                    disabled={service.status === 'stopped' || actionLoading === service.id}
                    className="flex items-center gap-1"
                    aria-label={`Stop ${service.name}`}
                  >
                    <Square className="h-4 w-4" aria-hidden="true" />
                    Stop
                  </Button>
                  
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleServiceAction(service.id, 'restart')}
                    disabled={actionLoading === service.id}
                    className="flex items-center gap-1"
                    aria-label={`Restart ${service.name}`}
                  >
                    <RotateCcw className="h-4 w-4" aria-hidden="true" />
                    Restart
                  </Button>
                </div>
                
                {actionLoading === service.id && (
                  <div className="mt-2 text-sm text-blue-600" role="status" aria-live="polite">
                    Processing...
                  </div>
                )}
              </div>
            ))
          )}
        </div>
        
        {/* Accessibility Summary */}
        <div className="mt-6 p-4 bg-gray-50 rounded-lg" role="complementary" aria-label="Service control information">
          <h4 className="font-medium text-gray-900 mb-2">Service Control Information</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• Use Tab to navigate between services</li>
            <li>• Press Enter or Space to select a service</li>
            <li>• Use arrow keys to navigate between action buttons</li>
            <li>• Service status is announced automatically</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}; 